import cn from 'classnames';
import { useCallback } from 'react';
import { ChannelPreview, ChannelPreviewUIComponentProps } from 'stream-chat-react';
import type { SearchResultItemProps } from 'stream-chat-react';
import type { Channel } from 'stream-chat';
import type { MessageForPreview } from './CustomPreviewMessenger/types';
import { getSearchMessage } from './searchMessageMap';
import { useChannelPreviewRefresh } from '../hooks/useChannelPreviewRefresh';
import { CustomPreviewMessenger } from './CustomPreviewMessenger';

function isChannel(r: SearchResultItemProps['result']): r is Channel {
  return !!r && typeof r === 'object' && 'cid' in r;
}

type CustomSearchResultItemProps = SearchResultItemProps & {
  searchQuery?: string;
};

const ChannelSearchResult = ({
  channel,
  index,
  focusedUser,
  selectResult,
  searchQuery,
}: {
  channel: Channel;
  index: SearchResultItemProps['index'];
  focusedUser: SearchResultItemProps['focusedUser'];
  selectResult: SearchResultItemProps['selectResult'];
  searchQuery?: string;
}) => {
  const isFocused = focusedUser === index;
  const channelUpdateCount = useChannelPreviewRefresh(channel.cid!);

  const Preview = useCallback(
    (p: ChannelPreviewUIComponentProps) => {
      const searchMessage = p.channel ? getSearchMessage(p.channel as Channel) : undefined;
      const message = (searchMessage ?? p.lastMessage) as MessageForPreview | undefined;
      return <CustomPreviewMessenger {...p} message={message} searchQuery={searchQuery} />;
    },
    [searchQuery],
  );

  const onChannelSelect = async () => {
    const searchMessage = getSearchMessage(channel);
    if (searchMessage) {
      await channel.state.loadMessageIntoState(searchMessage.id);
    }
    selectResult(channel);
  };

  return (
    <ChannelPreview
      className={cn('str-chat__search-result', {
        'str-chat__channel-search-result--focused': isFocused,
      })}
      channel={channel}
      channelUpdateCount={channelUpdateCount}
      Preview={Preview}
      onSelect={onChannelSelect}
    />
  );
};

export const SearchResultItem = ({
  result,
  index,
  focusedUser,
  selectResult,
  searchQuery,
}: CustomSearchResultItemProps) => {
  if (!isChannel(result)) return null;
  return (
    <ChannelSearchResult
      channel={result}
      index={index}
      focusedUser={focusedUser}
      selectResult={selectResult}
      searchQuery={searchQuery}
    />
  );
};

export default SearchResultItem;
